import { useState, useEffect } from "react";
import {
  getSettings,
  updateSettings,
  listProviderImages,
  listProviderSshKeys,
  listPlans,
} from "../../lib/api/cloud";
import type { Setting, ProviderImage, ProviderSshKey, ResourcePlan } from "../../types/cloud";

interface SelectOption {
  value: string;
  label: string;
}

interface SettingConfigBase {
  key: string;
  label: string;
  description: string;
}

interface TextSettingConfig extends SettingConfigBase {
  type: "text" | "password" | "readonly";
}

interface SelectSettingConfig extends SettingConfigBase {
  type: "select";
  fetchOptions: () => Promise<SelectOption[]>;
}

type SettingConfig = TextSettingConfig | SelectSettingConfig;

function formatImageOption(image: ProviderImage): SelectOption {
  const custom = image.isCustom ? " [Custom]" : "";
  const label = `${image.name} — ${image.os} ${image.version}${custom}`;
  return { value: image.id, label };
}

function formatSshKeyOption(key: ProviderSshKey): SelectOption {
  return { value: key.id, label: `${key.name} (ID: ${key.id})` };
}

function formatPlanOption(plan: ResourcePlan): SelectOption {
  const label = `${plan.name} — ${plan.cpuCores} CPU / ${plan.ramMb}MB RAM / ${plan.diskGb}GB`;
  return { value: plan.id, label };
}

async function fetchImageOptions(): Promise<SelectOption[]> {
  const images = await listProviderImages();
  return images.map(formatImageOption);
}

async function fetchSshKeyOptions(): Promise<SelectOption[]> {
  const keys = await listProviderSshKeys();
  return keys.map(formatSshKeyOption);
}

async function fetchPlanOptions(): Promise<SelectOption[]> {
  const res = await listPlans();
  return res.plans.map(formatPlanOption);
}

const SETTING_CONFIGS: SettingConfig[] = [
  {
    key: "golden_image_id",
    label: "Golden Image",
    description: "Timeweb image for VM creation",
    type: "select",
    fetchOptions: fetchImageOptions,
  },
  {
    key: "admin_ssh_key_id",
    label: "Admin SSH Key",
    description: "SSH key added to all VMs",
    type: "select",
    fetchOptions: fetchSshKeyOptions,
  },
  {
    key: "domain_base",
    label: "Domain Base",
    description: "Base domain for instances",
    type: "readonly",
  },
  {
    key: "dns_zone",
    label: "DNS Zone",
    description: "DNS zone for instance records",
    type: "readonly",
  },
  {
    key: "default_plan_id",
    label: "Default Plan",
    description: "Plan auto-assigned to new approved users",
    type: "select",
    fetchOptions: fetchPlanOptions,
  },
];

interface SelectFieldState {
  options: SelectOption[];
  loading: boolean;
  error: boolean;
}

export function PlatformSettings() {
  const [settings, setSettings] = useState<Map<string, unknown>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState<Map<string, unknown>>(new Map());
  const [saved, setSaved] = useState(false);
  const [selectStates, setSelectStates] = useState<Map<string, SelectFieldState>>(new Map());

  useEffect(() => {
    loadSettings();
    loadSelectOptions();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getSettings();
      const map = new Map<string, unknown>();
      res.settings.forEach((s: Setting) => map.set(s.key, s.value));
      setSettings(map);
    } catch {
      // Error handling
    } finally {
      setLoading(false);
    }
  };

  const loadSelectOptions = async () => {
    const selectConfigs = SETTING_CONFIGS.filter(
      (c): c is SelectSettingConfig => c.type === "select"
    );

    // Initialize all select states as loading
    const initial = new Map<string, SelectFieldState>();
    for (const config of selectConfigs) {
      initial.set(config.key, { options: [], loading: true, error: false });
    }
    setSelectStates(initial);

    // Fetch all options in parallel
    const results = await Promise.allSettled(
      selectConfigs.map(async (config) => {
        const options = await config.fetchOptions();
        return { key: config.key, options };
      })
    );

    setSelectStates((prev) => {
      const next = new Map(prev);
      for (const result of results) {
        if (result.status === "fulfilled") {
          next.set(result.value.key, {
            options: result.value.options,
            loading: false,
            error: false,
          });
        } else {
          // Find the key for the failed promise by index
          const idx = results.indexOf(result);
          const key = selectConfigs[idx].key;
          next.set(key, { options: [], loading: false, error: true });
        }
      }
      return next;
    });
  };

  const handleChange = (key: string, value: string) => {
    const newDirty = new Map(dirty);
    newDirty.set(key, value);
    setDirty(newDirty);
    setSaved(false);
  };

  const getValue = (key: string): string => {
    if (dirty.has(key)) return String(dirty.get(key));
    const val = settings.get(key);
    return val !== undefined ? String(val) : "";
  };

  const handleSave = async () => {
    if (dirty.size === 0) return;
    setSaving(true);
    try {
      const updates: Record<string, unknown> = {};
      dirty.forEach((value, key) => {
        updates[key] = value;
      });
      await updateSettings(updates);
      await loadSettings();
      setDirty(new Map());
      setSaved(true);
    } catch {
      // Error handling
    } finally {
      setSaving(false);
    }
  };

  const renderField = (config: SettingConfig) => {
    const value = getValue(config.key);

    if (config.type === "select") {
      const state = selectStates.get(config.key);
      const isLoading = state?.loading ?? true;
      const hasError = state?.error ?? false;

      // Fall back to text input if fetch failed
      if (hasError) {
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(config.key, e.target.value)}
            className="w-full px-3 py-2 border border-[#333] rounded text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500/50
              bg-[#111] text-[#f5f5f5]"
            placeholder="Enter ID manually (failed to load options)"
          />
        );
      }

      if (isLoading) {
        return (
          <div className="w-full px-3 py-2 border border-[#333] rounded text-sm bg-[#111] text-[#666] flex items-center gap-2">
            <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin" />
            Loading options...
          </div>
        );
      }

      const options = state?.options ?? [];
      return (
        <select
          value={value}
          onChange={(e) => handleChange(config.key, e.target.value)}
          className="w-full px-3 py-2 border border-[#333] rounded text-sm
            focus:outline-none focus:ring-2 focus:ring-blue-500/50
            bg-[#111] text-[#f5f5f5] appearance-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 12px center",
            paddingRight: "36px",
          }}
        >
          <option value="">Not set</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={config.type === "password" ? "password" : "text"}
        value={value}
        onChange={(e) => handleChange(config.key, e.target.value)}
        readOnly={config.type === "readonly"}
        className={`w-full px-3 py-2 border border-[#333] rounded text-sm
          focus:outline-none focus:ring-2 focus:ring-blue-500/50
          ${config.type === "readonly" ? "bg-[#0a0a0a] text-[#666] cursor-not-allowed" : "bg-[#111] text-[#f5f5f5]"}`}
        placeholder={config.type === "readonly" ? "cc.somebugs.com" : ""}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#f5f5f5]">Platform Settings</h1>
        <p className="text-[#999] text-sm mt-1">Configure cloud infrastructure settings</p>
      </div>

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 space-y-6">
        {SETTING_CONFIGS.map((config) => (
          <div key={config.key}>
            <label className="block text-[#f5f5f5] text-sm font-medium mb-0.5">
              {config.label}
            </label>
            <p className="text-[#666] text-xs mb-1.5">{config.description}</p>
            {renderField(config)}
          </div>
        ))}

        <div className="flex items-center justify-between pt-4 border-t border-[#333]">
          <div>
            {saved && <span className="text-green-400 text-sm">Settings saved successfully</span>}
            {dirty.size > 0 && !saved && (
              <span className="text-yellow-400 text-sm">{dirty.size} unsaved change(s)</span>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={dirty.size === 0 || saving}
            className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-40 transition-colors"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
